import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';
import { useAPI } from '../contexts/APIContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { usePageTracking, useAnalytics } from '../hooks/useAnalytics';
import { showToast } from '../components/Toast';
import ConfirmationModal from '../components/ConfirmationModal';
import PaywallModal from '../components/PaywallModal';
import { useEntitlementGate } from "../contexts/EntitlementGateContext";
import { saveCreationDraft, takeCreationDraft, fileFromDataUrl } from "../utils/creationDraft";
import VoicePicker from '../components/character/VoicePicker';
import VoiceRecorder from '../components/character/VoiceRecorder';
import { pendingVoiceUploads } from '../components/character/pendingVoice';

const CreateCharacter = () => {
  usePageTracking('Create Character');
  const { apiClient } = useAPI();
  const { user, costs, updateUser, subscriptionStatus } = useAuth();
  const { requireEntitlement } = useEntitlementGate();
  // Wall-interrupted draft (drawing / upload / prompt); restored when
  // checkout returns to this page.
  const creationDraftRef = useRef(takeCreationDraft("create-character"));

  useEffect(() => {
    const d = creationDraftRef.current;
    if (!d) return;
    if (d.mode) setMode(d.mode);
    if (d.characterName) setCharacterName(d.characterName);
    if (d.promptText) setPromptText(d.promptText);
    if (d.uploadedPreview) {
      setUploadedPreview(d.uploadedPreview);
      fileFromDataUrl(d.uploadedPreview, "restored-upload.png")
        .then(setUploadedImage)
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { trackCharacterCreationStarted } = useAnalytics();
  const navigate = useNavigate();

  // Mode: 'prompt' (default) or 'draw'
  const [mode, setMode] = useState('prompt');

  // Redraw the saved sketch once the draw-mode canvas exists.
  // Must sit AFTER mode is declared — the dependency array evaluates during
  // render and would TDZ-crash otherwise (same bug as the other create pages).
  useEffect(() => {
    const d = creationDraftRef.current;
    if (!d?.drawingDataUrl || mode !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = d.drawingDataUrl;
    creationDraftRef.current = { ...d, drawingDataUrl: null };
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared state
  const [characterName, setCharacterName] = useState('');
  const [voiceChoice, setVoiceChoice] = useState({ type: 'random' }); // { type: 'random' } | { type: 'preset', voice_id } | { type: 'recorded', file }
  const [voiceRecorderOpen, setVoiceRecorderOpen] = useState(false);
  // What the backend is told: a recording becomes "cloned" (the character
  // keeps an auto-picked voice until the recording is uploaded).
  const apiVoiceChoice = voiceChoice?.type === 'recorded' ? { type: 'cloned' } : voiceChoice;
  const [isCreating, setIsCreating] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCreation, setPendingCreation] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [paywallStarCost, setPaywallStarCost] = useState(0);
  const [paywallCurrentStars, setPaywallCurrentStars] = useState(0);

  // Prompt mode state
  const [promptText, setPromptText] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Draw mode state
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(5);
  const [brushColor, setBrushColor] = useState('#000000');
  const [drawingHistory, setDrawingHistory] = useState([]);

  // Initialize canvas when switching to draw mode
  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (drawingHistory.length > 0) {
      ctx.putImageData(drawingHistory[drawingHistory.length - 1], 0, 0);
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setDrawingHistory([ctx.getImageData(0, 0, canvas.width, canvas.height)]);
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [mode]);

  // Drawing functions
  const getCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const startDrawing = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushSize;
    setIsDrawing(true);
  }, [getCoordinates, brushColor, brushSize]);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [isDrawing, getCoordinates]);

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      setDrawingHistory(prev => [...prev, ctx.getImageData(0, 0, canvas.width, canvas.height)]);
    }
  }, [isDrawing]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setDrawingHistory([ctx.getImageData(0, 0, canvas.width, canvas.height)]);
  };

  const undoDrawing = () => {
    if (drawingHistory.length <= 1) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const newHistory = drawingHistory.slice(0, -1);
    ctx.putImageData(newHistory[newHistory.length - 1], 0, 0);
    setDrawingHistory(newHistory);
  };

  const hasDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < imageData.data.length; i += 4) {
      if (imageData.data[i] !== 255 || imageData.data[i + 1] !== 255 || imageData.data[i + 2] !== 255) return true;
    }
    return false;
  };

  // Image upload handling
  const handleImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploadedImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setUploadedPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleImageFile(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);

  const removeUploadedImage = () => {
    setUploadedImage(null);
    setUploadedPreview(null);
  };

  // Submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!user?.id) { showToast('Please sign in again.', 'error'); return; }
    if (!characterName.trim()) { showToast('Please enter a character name', 'error'); return; }

    if (mode === 'prompt') {
      if (!promptText.trim() && !uploadedImage) {
        showToast('Please describe your character or upload an image', 'error');
        return;
      }
    } else {
      if (!hasDrawing()) {
        showToast('Please draw something on the canvas', 'error');
        return;
      }
    }

    // Members-only gate first (no-op pre-cutover).
    if (!requireEntitlement({ trigger: "creation", contentType: "character" })) {
      let drawingDataUrl = null;
      try {
        if (mode === "draw" && canvasRef.current) {
          drawingDataUrl = canvasRef.current.toDataURL("image/png");
        }
      } catch { /* tainted canvas — skip the sketch */ }
      saveCreationDraft("create-character", {
        mode,
        characterName,
        promptText,
        uploadedPreview: uploadedPreview || null,
        drawingDataUrl,
      });
      return;
    }

    const starCost = costs?.create_character;
    if (starCost && starCost > 0) {
      const currentStars = user?.pixley_stars ?? 0;
      if (currentStars < starCost) {
        setPaywallStarCost(starCost);
        setPaywallCurrentStars(currentStars);
        setShowPaywallModal(true);
        return;
      }
      setPendingCreation(true);
      setShowConfirmModal(true);
      return;
    }

    await proceedWithCreation();
  };

  const proceedWithCreation = async () => {
    try {
      setIsCreating(true);
      setShowConfirmModal(false);

      // Deduct stars if needed
      const starCost = costs?.create_character;
      if (starCost && starCost > 0 && pendingCreation) {
        try {
          const resp = await apiClient.useStars(starCost, "create_character");
          if (resp?.status === "success" || resp?.status === true) {
            const nextStars =
              resp?.remaining_stars ??
              resp?.data?.remaining_stars ??
              resp?.data?.pixley_stars;
            if (nextStars !== undefined) {
              updateUser({ ...user, pixley_stars: nextStars });
            }
            showToast(`✨ ${starCost} stars deducted! Creating your character...`, 'success');
          } else {
            const errorMessage = resp?.message || resp?.error || 'Character creation failed';
            showToast(errorMessage, 'error');
            setIsCreating(false);
            setPendingCreation(false);
            return;
          }
        } catch (error) {
          showToast(error.message || 'Character creation failed', 'error');
          setIsCreating(false);
          setPendingCreation(false);
          return;
        }
      }

      let response;

      if (mode === 'prompt') {
        // Prompt mode: text and/or uploaded image
        response = await apiClient.createCharacterWithAnimation({
          user_id: user.id,
          name: characterName.trim(),
          image: uploadedImage || undefined,
          handdrawn: false,
          prompt: promptText.trim() || undefined,
          voice_choice: apiVoiceChoice,
        });
      } else {
        // Draw mode: canvas blob
        const canvas = canvasRef.current;
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
        response = await apiClient.createCharacterWithAnimation({
          user_id: user.id,
          name: characterName.trim(),
          image: blob,
          handdrawn: true,
          voice_choice: apiVoiceChoice,
        });
      }

      console.log("CREATE RESPONSE:", response);
      if (response?.status === "success" || response?.status === "processing" || response?.success === true) {
        if (response?.remaining_stars !== undefined) {
          updateUser({ ...user, pixley_stars: response.remaining_stars });
        }
        trackCharacterCreationStarted({
          method: mode,
          name: characterName.trim(),
          hasImage: !!uploadedImage,
        });
        showToast(response?.message || "Character created successfully", 'success');
        if (response?.character_id) {
          // The character row appears once its picture is drawn; its page
          // uploads the recording as soon as it loads the character.
          if (voiceChoice?.type === 'recorded' && voiceChoice.file) {
            pendingVoiceUploads.set(response.character_id, voiceChoice.file);
          }
          navigate(`/characters/${response.character_id}`);
        } else {
          navigate('/characters');
        }
      } else {
        showToast(response?.message || 'Character creation failed', 'error');
        setIsCreating(false);
      }
    } catch (error) {
      logger.error('Error creating character:', error);
      showToast(error.message || 'Failed to create character', 'error');
      setIsCreating(false);
      setPendingCreation(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 50%, #0f0f23 100%)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-28 pb-16">

        {/* Header */}
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <button onClick={() => navigate('/characters')} className="flex items-center gap-2 text-white/50 hover:text-white/80 transition-colors mb-4 text-sm">
            <span>←</span> Back to Characters
          </button>
          <h1 className="text-3xl font-bold text-white font-fredoka">Create New Character</h1>
          <p className="text-white/50 mt-1">Describe, upload, or draw your character</p>
        </motion.div>

        {/* Character Name */}
        <motion.div className="mb-6" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2 ml-1">Character Name</label>
          <input
            type="text"
            value={characterName}
            onChange={(e) => setCharacterName(e.target.value)}
            className="w-full p-4 bg-white/5 border border-white/10 rounded-2xl text-white text-lg font-medium placeholder-white/30 focus:border-[#4B3BE5]/50 focus:ring-2 focus:ring-[#4B3BE5]/20 focus:outline-none transition-all"
            placeholder="Enter your character's name..."
          />
        </motion.div>

        <AnimatePresence mode="wait">
          {mode === 'prompt' ? (
            <motion.div key="prompt" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>

              {/* Prompt + Upload Area */}
              <div
                className={`relative rounded-2xl border-2 transition-all ${isDragOver ? 'border-[#4B3BE5] bg-[#4B3BE5]/10' : 'border-white/10 bg-white/5'}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <textarea
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  className="w-full min-h-[200px] p-6 pb-16 bg-transparent text-white text-base placeholder-white/30 focus:outline-none resize-none"
                  placeholder="Describe your character... e.g., A friendly blue dragon with golden wings who loves to bake cookies"
                />

                {/* Uploaded image preview */}
                {uploadedPreview && (
                  <div className="px-6 pb-4">
                    <div className="inline-flex items-center gap-3 bg-white/10 rounded-xl p-2 pr-4">
                      <img src={uploadedPreview} alt="Upload" className="w-16 h-16 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white/80 text-sm font-medium truncate">{uploadedImage?.name}</p>
                        <p className="text-white/40 text-xs">{(uploadedImage?.size / 1024).toFixed(0)} KB</p>
                      </div>
                      <button onClick={removeUploadedImage} className="text-white/40 hover:text-red-400 transition-colors p-1">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Bottom bar: upload button + drag hint */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/15 rounded-xl text-white/60 hover:text-white/80 transition-all text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Upload Image
                  </button>
                  <span className="text-white/20 text-xs hidden sm:block">or drag & drop an image</span>
                </div>

                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageFile(e.target.files[0])} />
              </div>

              {/* Switch to draw mode */}
              <div className="mt-4 text-center">
                <button onClick={() => setMode('draw')} className="text-[#4B3BE5] hover:text-[#6B5EF1] text-sm font-medium transition-colors">
                  🎨 Or draw your character instead
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div key="draw" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>

              {/* Back to prompt */}
              <button onClick={() => setMode('prompt')} className="flex items-center gap-2 text-[#4B3BE5] hover:text-[#6B5EF1] text-sm font-medium mb-4 transition-colors">
                ← Back to text description
              </button>

              {/* Drawing Canvas */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold">Drawing Canvas</h3>
                  <div className="flex gap-2">
                    <button onClick={clearCanvas} className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-colors">Clear</button>
                    <button onClick={undoDrawing} className="px-3 py-1.5 bg-white/10 hover:bg-white/15 text-white/70 rounded-lg text-sm font-medium transition-colors">Undo</button>
                  </div>
                </div>

                <div className="flex justify-center mb-4">
                  <div className="bg-white rounded-xl p-1" style={{ touchAction: 'none' }}>
                    <canvas
                      ref={canvasRef}
                      width={400}
                      height={400}
                      className="rounded-lg cursor-crosshair"
                      style={{ touchAction: 'none', maxWidth: '100%', height: 'auto' }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                </div>

                {/* Brush tools */}
                <div className="flex justify-center gap-6 items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-white/50 text-sm">Size:</span>
                    <input type="range" min="1" max="20" value={brushSize} onChange={(e) => setBrushSize(e.target.value)} className="w-20 accent-[#4B3BE5]" />
                    <span className="text-white/40 text-xs w-8">{brushSize}px</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-white/50 text-sm">Color:</span>
                    <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} className="w-8 h-8 rounded-lg cursor-pointer border-0" />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Voice */}
        <motion.div className="mt-8" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-1 ml-1">Voice</label>
          <p className="text-white/50 text-sm mb-3 ml-1">
            {voiceChoice?.type === 'preset' && voiceChoice.name
              ? <>They'll sound like <span className="text-white font-semibold">{voiceChoice.name}</span> in stories, storybooks and calls.</>
              : voiceChoice?.type === 'recorded'
                ? <>They'll speak with <span className="text-white font-semibold">your recording</span> in stories, storybooks and calls.</>
                : <>Pip will pick a voice that fits. Or tap one to hear it.</>}
          </p>
          <VoicePicker value={voiceChoice} onChange={setVoiceChoice} onRecord={() => setVoiceRecorderOpen(true)} />
          <VoiceRecorder
            isOpen={voiceRecorderOpen}
            onClose={() => setVoiceRecorderOpen(false)}
            onDone={(file) => { setVoiceRecorderOpen(false); setVoiceChoice({ type: 'recorded', file }); }}
          />
        </motion.div>

        {/* Create Button */}
        <motion.div className="mt-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <button
            onClick={handleSubmit}
            disabled={isCreating}
            className="w-full py-4 bg-gradient-to-r from-[#4B3BE5] to-[#6B5EF1] text-white font-bold text-lg rounded-2xl hover:shadow-lg hover:shadow-[#4B3BE5]/20 active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </span>
            ) : (
              'Create Character'
            )}
          </button>
          <button onClick={() => navigate('/characters')} className="w-full mt-3 py-3 text-white/40 hover:text-white/60 font-medium transition-colors text-sm">
            Cancel
          </button>
        </motion.div>
      </div>

      {/* Modals */}
      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => { setShowConfirmModal(false); setPendingCreation(false); }}
          onConfirm={proceedWithCreation}
          title="Create Character"
          message={`This will cost ${costs?.create_character || 0} stars. Continue?`}
          confirmText="Create"
          cancelText="Cancel"
        />
      )}
      {showPaywallModal && (
        <PaywallModal
          isOpen={showPaywallModal}
          onClose={() => setShowPaywallModal(false)}
          starCost={paywallStarCost}
          currentStars={paywallCurrentStars}
        />
      )}
    </div>
  );
};

export default CreateCharacter;
